import { StockItem, StockPackage, StockTransaction } from '../types/stock.js';

/**
 * Utility for mapping between Supabase Database rows and Frontend Stock objects.
 * Centralizing this ensures that schema changes only need to be updated in one place.
 */

// SEC-M7 phase 3: explicit column allowlist for `stock_items` reads. Keep in
// sync with `dbRowToStockItem`.
export const STOCK_ITEM_COLUMNS =
  'id, team_id, name, description, unit, image_url, current_quantity, ' +
  'package_size, storage_location_id, active_location_id, active_quantity, ' +
  'reorder_threshold, refill_threshold, expiry_date, expiry_warning_days, ' +
  'category, category_id, tag_ids, bundle_unit, webshop_links, metadata, ' +
  'last_checked_at, par_level, favourite, created_at, updated_at';

export const dbRowToStockItem = (row: any): StockItem => {
  return {
    id: row.id,
    teamId: row.team_id,
    name: row.name,
    description: row.description || undefined,
    unit: row.unit,
    imageUrl: row.image_url || undefined,
    currentQuantity: row.current_quantity,
    packageSize: row.package_size,
    storageLocationId: row.storage_location_id || undefined,
    activeLocationId: row.active_location_id || undefined,
    activeQuantity: row.active_quantity,
    reorderThreshold: row.reorder_threshold,
    refillThreshold: row.refill_threshold,
    expiryDate: row.expiry_date ? new Date(row.expiry_date) : undefined,
    expiryWarningDays: row.expiry_warning_days ?? 14,
    category: row.category ?? 'algemeen',
    categoryId: row.category_id || undefined,
    tagIds: row.tag_ids ?? [],
    bundleUnit: row.bundle_unit ?? 'doos',
    webshopLinks: row.webshop_links ?? [],
    metadata: row.metadata ?? {},
    lastCheckedAt: row.last_checked_at ? new Date(row.last_checked_at) : undefined,
    parLevel: row.par_level !== null && row.par_level !== undefined ? Number(row.par_level) : undefined,
    favourite: row.favourite ?? false,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  };
};

export const mapStockItemToDb = (updates: Partial<StockItem>): any => {
  const dbUpdates: any = {};

  if ('name' in updates) dbUpdates.name = updates.name;
  if ('description' in updates) dbUpdates.description = updates.description;
  if ('unit' in updates) dbUpdates.unit = updates.unit;
  if ('imageUrl' in updates) dbUpdates.image_url = updates.imageUrl;
  if ('currentQuantity' in updates) dbUpdates.current_quantity = updates.currentQuantity;
  if ('packageSize' in updates) dbUpdates.package_size = updates.packageSize;
  if ('storageLocationId' in updates) dbUpdates.storage_location_id = updates.storageLocationId;
  if ('activeLocationId' in updates) dbUpdates.active_location_id = updates.activeLocationId;
  if ('activeQuantity' in updates) dbUpdates.active_quantity = updates.activeQuantity;
  if ('reorderThreshold' in updates) dbUpdates.reorder_threshold = updates.reorderThreshold;
  if ('refillThreshold' in updates) dbUpdates.refill_threshold = updates.refillThreshold;
  if ('expiryDate' in updates) dbUpdates.expiry_date = updates.expiryDate ? (updates.expiryDate instanceof Date ? updates.expiryDate.toISOString() : updates.expiryDate) : null;
  if ('expiryWarningDays' in updates) dbUpdates.expiry_warning_days = updates.expiryWarningDays;
  if ('category' in updates) dbUpdates.category = updates.category;
  if ('categoryId' in updates) dbUpdates.category_id = updates.categoryId;
  if ('tagIds' in updates) dbUpdates.tag_ids = updates.tagIds;
  if ('bundleUnit' in updates) dbUpdates.bundle_unit = updates.bundleUnit;
  if ('webshopLinks' in updates) dbUpdates.webshop_links = updates.webshopLinks;
  if ('metadata' in updates) dbUpdates.metadata = updates.metadata;
  if ('lastCheckedAt' in updates) dbUpdates.last_checked_at = updates.lastCheckedAt
    ? (updates.lastCheckedAt instanceof Date ? updates.lastCheckedAt.toISOString() : updates.lastCheckedAt)
    : null;
  if ('parLevel' in updates) dbUpdates.par_level = updates.parLevel ?? null;
  if ('favourite' in updates) dbUpdates.favourite = updates.favourite;

  return dbUpdates;
};

// SEC-M7 phase 17: explicit column allowlist for `stock_packages` reads. Keep
// in sync with `dbRowToPackage` and the local `mapRowToPackage` in
// `useStockPackages.ts` (which additionally consumes `discard_date` →
// `expectedDeliveryDate`). Replacing `.select('*')` prevents future internal
// columns (e.g. supplier metadata, batch/lot tracking, internal cost-allocation
// fields, audit/fraud flags) from auto-flowing to every team member.
export const STOCK_PACKAGE_COLUMNS =
  'id, stock_item_id, team_id, name, quantity, original_quantity, ' +
  'location_id, status, purchase_price, purchase_date, expiry_date, ' +
  'discard_date, notes, image_url, purchase_id, store_ids, ' +
  'created_at, updated_at';

export const dbRowToPackage = (row: any): StockPackage => {
  return {
    id: row.id,
    stockItemId: row.stock_item_id,
    teamId: row.team_id,
    name: row.name || undefined,
    quantity: row.quantity,
    originalQuantity: row.original_quantity,
    locationId: row.location_id || undefined,
    status: row.status,
    purchasePrice: row.purchase_price || undefined,
    purchaseDate: row.purchase_date ? new Date(row.purchase_date) : undefined,
    expiryDate: row.expiry_date ? new Date(row.expiry_date) : undefined,
    notes: row.notes || undefined,
    imageUrl: row.image_url || undefined,
    purchaseId: row.purchase_id || undefined,
    storeIds: row.store_ids ?? [],
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  };
};

export const dbRowToTransaction = (row: any): StockTransaction => {
  return {
    id: row.id,
    stockItemId: row.stock_item_id,
    teamId: row.team_id,
    quantityChange: row.quantity_change,
    transactionType: row.transaction_type,
    notes: row.notes || undefined,
    relatedTaskId: row.related_task_id || undefined,
    createdByMemberId: row.created_by_member_id || undefined,
    createdByDeviceId: row.created_by_device_id || undefined,
    createdAt: new Date(row.created_at),
  };
};
